<script setup>
/**
 * Which ledger is open, at the top of Settings' Sync tab. Every ledger is an
 * org (stores/ledgers.js): this device's — readable or encrypted, as many as
 * one likes — and the account's in the cloud, either kind. Choosing one
 * reloads onto it, because boot is where a ledger is opened. A new ledger
 * starts here, readable; the square below moves it anywhere. A ledger of this
 * device can be deleted here (the cloud's, by an owner, in CloudPanel).
 */
import { computed, onMounted, ref } from 'vue'
import Icon from '../ui/Icon.vue'
import { state } from '../../stores/state'
import { errorText, t } from '../../stores/i18n'
import * as cloud from '../../stores/cloud'
import { addLedger, currentLedger, fileOf, ledgers, newOrgId, removeLedger, sealedNames, setCurrentLedger } from '../../stores/ledgers'
import { dropLater } from '../../db/drops'
import { mightBeSignedIn, setVaultOrg } from '../../stores/sync'

const inCloud = cloud.getMode() === 'cloud'
const here = inCloud ? null : currentLedger()
const account = ref(state.cloud.account)
const naming = ref(false)
const name = ref('')
const error = ref('')

onMounted(async () => {
  if (!account.value && mightBeSignedIn()) account.value = await cloud.me().catch(() => null)
})

const options = computed(() => [
  ...ledgers().map((l) => ({ value: `local:${l.id}`, label: `${l.name || t('sync.defaultName')} · ${t(l.kind === 'encrypted' ? 'states.sealed' : 'states.local')}` })),
  ...(account.value?.orgs ?? []).map((o) => ({ value: `cloud:${o.id}`, label: `${o.name} · ${t(o.kind === 'vault' ? 'states.vault' : 'states.cloud')}` })),
])
const current = inCloud ? `cloud:${cloud.getOrgId()}` : `local:${here?.id}`

function open(value) {
  if (value === current) return
  const [where, id] = value.split(':')
  if (where === 'local') {
    setCurrentLedger(id)
    cloud.setMode('local')
  } else {
    // Boot opens it by its kind, and remembers an encrypted one again.
    setVaultOrg(null)
    cloud.setOrgId(id)
    cloud.setMode('cloud')
  }
  location.reload()
}

function create() {
  const ledger = addLedger({ id: newOrgId(), name: name.value.trim(), kind: 'plain' })
  setCurrentLedger(ledger.id)
  cloud.setMode('local')
  location.reload()
}

/** Deletes this device's open ledger — at the next start, before anything opens its file. */
function remove() {
  error.value = ''
  try {
    if (!confirm(t('ledgers.deleteConfirm', { name: here.name || t('sync.defaultName') }))) return
    if (here.kind === 'encrypted') {
      const { blob, keys } = sealedNames(here)
      dropLater(`sealed:${blob}|${keys}`)
    } else dropLater(fileOf(here))
    removeLedger(here.id)
    location.reload()
  } catch (err) {
    error.value = errorText(err)
  }
}
</script>

<template>
  <section class="grid gap-2" data-ledgers>
    <p class="text-[12.5px] font-semibold text-muted">{{ t('ledgers.title') }}</p>
    <select class="input" :value="current" :aria-label="t('ledgers.title')" data-ledger-picker @change="open($event.target.value)">
      <option v-for="o in options" :key="o.value" :value="o.value">{{ o.label }}</option>
    </select>
    <form v-if="naming" class="flex gap-2" @submit.prevent="create">
      <input v-model="name" class="input" :placeholder="t('sync.defaultName')" :aria-label="t('ledgers.name')" data-ledger-name />
      <button class="btn-soft shrink-0 whitespace-nowrap">{{ t('ledgers.create') }}</button>
    </form>
    <div class="flex flex-wrap gap-2">
      <button v-if="!naming" type="button" class="btn-ghost !text-[12.5px]" data-ledger-new @click="naming = true">
        <Icon name="plus" :size="14" /> {{ t('ledgers.new') }}
      </button>
      <button v-if="here" type="button" class="btn-ghost !text-[12.5px]" data-ledger-delete @click="remove">
        <Icon name="trash" :size="14" class="text-warn" /> {{ t('ledgers.delete') }}
      </button>
    </div>
    <p v-if="error" class="text-[12.5px] text-warn" role="alert">{{ error }}</p>
  </section>
</template>
